import {
  t,
  translate,
} from "components/CentralDataMangement/translation/Translation";
import Button from "components/common/atoms/Button";
import ModalPopup from "components/common/atoms/ModalPopup";
import SignatureField from "components/common/atoms/SignatureField";
import CustomNotify from "components/common/atoms/CustomNotify";
import LoadingIcon from "components/common/utlis/LoadingIcon";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";

/**
 * Public, no-login page where a candidate opens an emailed token link, reviews the
 * registration/consent (INS) form and signs it. Talks to the candidate backend
 * DIRECTLY (REACT_APP_BACKEND_SERVICE) - no api-gateway, no login/token headers.
 *
 * Flow: if the candidate has NO signature yet, the signature pad opens immediately
 * so they add it first. The drawn signature is saved (without finalizing), the
 * preview reloads showing the signature embedded in the form, and the candidate
 * just clicks Approve to finalize. If a signature already exists, the preview shows
 * it and they go straight to Approve.
 */
const PublicSignConsent = () => {
  const { token } = useParams<{ token: string }>();
  const lang_key = (secureLocalStorage.getItem("site_lang") as string) ?? "en";
  const backend = process.env.REACT_APP_BACKEND_SERVICE;

  const [status, setStatus] = useState<
    "loading" | "valid" | "invalid" | "done"
  >("loading");
  const [signExists, setSignExists] = useState(false);
  const [signaturePadPopup, setSignaturePadPopup] = useState(false);
  // Bumped after saving a signature to force the preview iframe to reload so the
  // freshly stored signature is rendered in the form.
  const [previewVersion, setPreviewVersion] = useState(0);
  // Shows the page's loading spinner while a save/approve request is in flight.
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await fetch(`${backend}/api/public/consent/${token}`);
        const data = await res.json();
        if (data?.status === 200) {
          const exists = data?.data?.signExists === true;
          setSignExists(exists);
          setStatus("valid");
          // No signature yet -> add it first.
          if (!exists) {
            setSignaturePadPopup(true);
          }
        } else {
          setStatus("invalid");
        }
      } catch {
        setStatus("invalid");
      }
    };
    validate();
  }, [token, backend]);

  // Save the drawn signature WITHOUT finalizing, then reload the preview so the
  // candidate can review the form with their signature before approving.
  const saveSignature = async (signData: string) => {
    setSubmitting(true);
    try {
      const res = await fetch(
        `${backend}/api/public/consent/${token}/signature`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sign: signData }),
        }
      );
      const data = await res.json();
      if (data?.status === 200) {
        setSignaturePadPopup(false);
        setSignExists(true);
        setPreviewVersion((v) => v + 1);
      } else {
        CustomNotify({
          type: "error",
          message: t(data?.msg ?? "Some error occured"),
        });
      }
    } catch {
      CustomNotify({ type: "error", message: t("Some error occured") });
    } finally {
      setSubmitting(false);
    }
  };

  // Finalize the consent using the candidate's stored signature.
  const approve = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${backend}/api/public/consent/${token}/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lang: lang_key }),
      });
      const data = await res.json();
      if (data?.status === 200) {
        setStatus("done");
      } else {
        CustomNotify({
          type: "error",
          message: t(data?.msg ?? "Some error occured"),
        });
      }
    } catch {
      CustomNotify({ type: "error", message: t("Some error occured") });
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "loading") {
    return <div className="text-center p-5">{t("Loading...")}</div>;
  }
  if (status === "invalid") {
    return (
      <div className="text-center p-5">
        {t("This signing link is no longer valid.")}
      </div>
    );
  }
  if (status === "done") {
    return (
      <div className="text-center p-5">
        {t("Thank you. Your registration form has been signed.")}
      </div>
    );
  }

  return (
    <div className="container py-4">
      {submitting && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.6)",
            zIndex: 2000,
          }}
        >
          <LoadingIcon iconType="bars" color="#e55496" />
        </div>
      )}
      <iframe
        width="100%"
        src={`${backend}/api/public/consent/${token}/preview?lang=${lang_key}&v=${previewVersion}`}
        title="Registration form"
        className="iframeHeight"
      ></iframe>

      <div className="d-flex justify-content-end gap-2 mt-3">
        {!signExists && (
          <Button
            title={t("Add signature")}
            handleClick={() => setSignaturePadPopup(true)}
            className="form-button shadow-none"
          />
        )}
        {signExists && (
          <Button
            title={t("Approve")}
            handleClick={approve}
            className="form-button shadow-none"
          />
        )}
      </div>

      <ModalPopup
        show={signaturePadPopup}
        onHide={() => setSignaturePadPopup(false)}
        title={t("Add your signature")}
        body={
          <SignatureField
            className={"signatureCanvas"}
            onSave={(d: string) => saveSignature(d)}
            width={"100%"}
          />
        }
        className="modal-lg"
      />
    </div>
  );
};

export default translate(PublicSignConsent);
